import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { getAdminGrowthCharts } from '../api/growthCharts';
import type {
  AdminGrowthCharts,
  GrowthSeries,
} from '../types/growthCharts';
import styles from './GrowthCharts.module.css';

/**
 * Growth Charts page.
 *
 * Ports the per-day growth charts from the Django admin index into the
 * React dashboard, with the same six time-series + the free-to-paid
 * conversion-rate stat block. The Django admin caps the window at 90
 * days; here we let the user pick up to a full year.
 *
 * The page drives all charts from a single `days` selector and a single
 * GET to `/api/admin/growth/`. Each chart renders an AreaChart instead of
 * a line+dot chart because at 365 days the dot density on the small
 * stock chart becomes unreadable, and the area also makes "magnitude
 * over time" easier to skim.
 */

const WINDOWS: ReadonlyArray<{ label: string; days: number }> = [
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
  { label: '6m', days: 180 },
  { label: '1y', days: 365 },
];

const TOOLTIP_STYLE = {
  background: '#0d0f14',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 6,
  fontSize: '0.8125rem',
} as const;

function fmtNumber(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '—';
  return n.toLocaleString();
}

function fmtPercent(n: number | null | undefined, digits = 1): string {
  if (n == null || Number.isNaN(n)) return '—';
  return `${n.toFixed(digits)}%`;
}

function formatRelativeTime(date: Date | null): string {
  if (!date) return 'Never';
  const now = new Date();
  const diffSec = Math.floor((now.getTime() - date.getTime()) / 1000);
  if (diffSec < 5) return 'Just now';
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  return `${Math.floor(diffMin / 60)}h ago`;
}

// Tick-thinning: at 365 daily points the default Recharts behaviour
// crams labels on top of each other. We aim for ~12 visible ticks
// regardless of window size so the X axis stays scannable.
function tickInterval(pointCount: number): number {
  if (pointCount <= 12) return 0;
  return Math.max(Math.floor(pointCount / 12) - 1, 0);
}

interface ChartPoint {
  label: string;
  value: number;
}

function seriesToPoints(series: GrowthSeries | undefined): ChartPoint[] {
  if (!series || !series.labels || !series.data) return [];
  const n = Math.min(series.labels.length, series.data.length);
  const out: ChartPoint[] = [];
  for (let i = 0; i < n; i++) {
    out.push({ label: series.labels[i], value: series.data[i] });
  }
  return out;
}

interface MetricChartProps {
  title: string;
  total: number;
  totalLabel: string;
  avgPerDay?: number | null;
  color: string;
  series: GrowthSeries | undefined;
  emptyMessage: string;
  note?: string;
}

function MetricChart({
  title,
  total,
  totalLabel,
  avgPerDay,
  color,
  series,
  emptyMessage,
  note,
}: MetricChartProps) {
  const points = useMemo(() => seriesToPoints(series), [series]);
  const interval = tickInterval(points.length);
  const gradientId = useMemo(
    () => `growth-grad-${title.replace(/\s+/g, '-').toLowerCase()}`,
    [title]
  );

  const empty = total === 0 && points.every((p) => p.value === 0);

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <h3 className={styles.sectionTitle}>{title}</h3>
        <div>
          <span className={styles.sectionTotal}>{fmtNumber(total)}</span>
          <span className={styles.sectionSubtotal}>{totalLabel}</span>
        </div>
      </div>
      {avgPerDay != null && (
        <div className={styles.subStat}>
          <strong>{avgPerDay.toFixed(1)}</strong> /day avg
        </div>
      )}
      {empty ? (
        <div className={styles.empty}>{emptyMessage}</div>
      ) : (
        <div className={styles.chartWrap}>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart
              data={points}
              margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={0.55} />
                  <stop offset="100%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="rgba(255,255,255,0.06)"
              />
              <XAxis
                dataKey="label"
                stroke="#9ca3af"
                tick={{ fontSize: 11 }}
                interval={interval}
              />
              <YAxis
                stroke="#9ca3af"
                tick={{ fontSize: 11 }}
                width={40}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={TOOLTIP_STYLE}
                labelStyle={{ color: '#e5e7eb' }}
                formatter={(value) => {
                  const n = typeof value === 'number' ? value : Number(value);
                  return [Number.isFinite(n) ? n.toLocaleString() : String(value), title];
                }}
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke={color}
                strokeWidth={2}
                fill={`url(#${gradientId})`}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
      {note && <div className={styles.chartNote}>{note}</div>}
    </div>
  );
}

export default function GrowthCharts() {
  const [days, setDays] = useState<number>(90);
  const [data, setData] = useState<AdminGrowthCharts | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const load = useCallback(async (windowDays: number) => {
    setLoading(true);
    setError(null);
    try {
      const result = await getAdminGrowthCharts(windowDays);
      setData(result);
      setLastUpdated(new Date());
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : 'Failed to load growth charts';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(days);
  }, [days, load]);

  const conversion = data?.conversion_rate;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Growth Charts</h1>
          <p className={styles.subtitle}>
            Daily user, subscription, and device activity across the rolling
            window. Mirrors the Django admin dashboard, extended out to a
            full year.
          </p>
        </div>
        <div className={styles.headerActions}>
          <span className={styles.lastUpdated}>
            Updated {formatRelativeTime(lastUpdated)}
          </span>
          <button
            type="button"
            className={styles.refreshButton}
            onClick={() => void load(days)}
            disabled={loading}
          >
            <span
              className={`${styles.refreshIcon} ${loading ? styles.spinning : ''}`}
            >
              ↻
            </span>
            Refresh
          </button>
        </div>
      </div>

      <div className={styles.windowBar}>
        <span className={styles.windowBarLabel}>Window</span>
        <div className={styles.windowPills}>
          {WINDOWS.map((w) => (
            <button
              key={w.days}
              type="button"
              onClick={() => setDays(w.days)}
              className={`${styles.pill} ${days === w.days ? styles.pillActive : ''}`}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {/* Conversion-rate stat block (no chart — overall + period stats) */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <div className={styles.statValue}>
            {fmtPercent(conversion?.overall_conversion_rate)}
          </div>
          <div className={styles.statLabel}>Overall conversion rate</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>
            {fmtPercent(conversion?.period_conversion_rate)}
          </div>
          <div className={styles.statLabel}>
            Period conversion ({conversion?.period_days ?? days}d)
          </div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>
            {fmtNumber(conversion?.total_trial_users)}
          </div>
          <div className={styles.statLabel}>Total trial users (all-time)</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statValue}>
            {fmtNumber(conversion?.total_conversions)}
          </div>
          <div className={styles.statLabel}>Total paid conversions</div>
        </div>
      </div>

      {loading && !data ? (
        <div className={styles.loading}>Loading growth charts…</div>
      ) : data ? (
        <>
          <div className={styles.chartsGrid}>
            <MetricChart
              title="User Registrations"
              total={data.user_registrations.total_users}
              totalLabel="new trials"
              color="#417690"
              series={data.user_registrations}
              emptyMessage="No new registrations in this window."
            />
            <MetricChart
              title="Daily Active Users"
              total={data.daily_active_users.total_active_users}
              totalLabel="user-days"
              color="#28a745"
              series={data.daily_active_users}
              emptyMessage="No tracked activity in this window."
              note={`${fmtNumber(data.daily_active_users.unique_active_users)} unique users active`}
            />
            <MetricChart
              title="New Paid Users"
              total={data.new_paid_users.total_new_paid_users}
              totalLabel="new subscriptions"
              color="#ffc107"
              series={data.new_paid_users}
              emptyMessage="No new paid subscriptions in this window."
            />
            <MetricChart
              title="Churn (cancellations)"
              total={data.churn.total_churned_users}
              totalLabel="cancellations"
              color="#dc3545"
              series={data.churn}
              emptyMessage="No cancellations in this window."
            />
            <MetricChart
              title="Device Links"
              total={data.device_links.total_links}
              totalLabel="new devices"
              avgPerDay={data.device_links.avg_per_day}
              color="#8b5cf6"
              series={data.device_links}
              emptyMessage="No new devices linked in this window."
            />
            <MetricChart
              title="Active Devices"
              total={data.active_devices.total_syncing_devices}
              totalLabel="device-days"
              avgPerDay={data.active_devices.avg_per_day}
              color="#10b981"
              series={data.active_devices}
              emptyMessage="No device sync activity in this window."
              note="Unique devices that hit the sync handshake endpoint that day."
            />
          </div>

          <div className={styles.notes}>
            <h2 className={styles.notesTitle}>Notes</h2>
            <ul>
              <li>
                <strong>Active Devices</strong> is now counted from{' '}
                <code>SyncEvent</code> rows (one per sync handshake). The
                Django admin version of this chart groups by{' '}
                <code>Device.last_synced_at</code>, which is a single field
                that gets overwritten on every sync — so every active device
                collapses onto its most-recent sync date and the latest day
                always looks artificially high. The chart above does not
                have that bias.
              </li>
              <li>
                <strong>Daily Active Users</strong> counts user-days against
                the <code>UserActivity</code> table; rows there are created
                the first time the user hits a tracked endpoint each day, so
                back-fill is limited to whatever has been logged since
                tracking was turned on.
              </li>
              <li>
                <strong>User Registrations</strong> uses{' '}
                <code>trial_start_date</code> as the registration anchor
                (same as the Django admin), so users who registered without
                starting a trial won't appear here.
              </li>
              <li>
                <strong>Churn</strong> counts subscriptions whose{' '}
                <code>canceled_at</code> falls in the window, regardless of
                whether the user later resubscribed.
              </li>
            </ul>
          </div>
        </>
      ) : null}
    </div>
  );
}
